import React from 'react';
import { CapabilityNoetivraNote } from './CapabilityNoetivraNote';
import { motion } from 'framer-motion';
import { Compass, Target, CheckCircle, XCircle } from 'lucide-react';
export function SelfDirectionPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1487" data-mp="2s86ta0exqeg7">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1488" data-mp="2nb6mtoe6b9ok">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1489" data-mp="2ibk9zg29bvga">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1490" data-mp="24bsneqxod6qo">
            <span className="w-8 h-px bg-teal" data-id="element-1491" data-mp="24ddm8sr0vbuc"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1492" data-mp="7jite2ydbzai">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1493" data-mp="2kpgabmzuaav"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1494" data-mp="e14gyhpu4ioy">
            Self-Direction and Execution
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1495" data-mp="51r73nxsts3p">
            At SyNeural, self-direction and execution refer to the ability to
            organize effort, follow through, maintain discipline, translate
            goals into action, and sustain progress over time without constant
            external pressure. It is the capability that turns intention into
            movement and potential into real output.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1496" data-mp="syn6vne911xz">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1497" data-mp="1gpao4za213x7">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1498" data-mp="1qc3kt57chvpm">
            This matters because many people have intelligence, creativity, or
            ambition without consistent execution. They may understand what
            should be done, but struggle to organize themselves, sustain
            momentum, or convert plans into results. Self-direction and
            execution therefore play a major role in whether potential becomes
            contribution, whether learning becomes action, and whether
            opportunity turns into progress.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1499" data-mp="xpiso2j1pfl0">
        <div className="max-w-4xl mx-auto" data-id="element-1500" data-mp="socx7wy03e7s">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1501" data-mp="on9g7747vc4z">
            <div className="flex items-center gap-4 mb-6" data-id="element-1502" data-mp="39cma5azru4b8">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1503" data-mp="3ea5oeawgyh36">
                <Target size={24} data-id="element-1504" data-mp="2gmx4gkc49g3a" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1505" data-mp="2gogn2hl8w388">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1506" data-mp="2qkbbxug2aujg">
              Without execution, many other strengths remain dormant. Weak
              self-direction can undermine education, work, leadership
              development, and personal stability even when talent is present.
              Strong execution improves reliability, momentum, confidence, and
              readiness for greater responsibility. It is one of the clearest
              bridges between capability and real-world performance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Self-Direction Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1507" data-mp="i5ljxpi3mbbg">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1508" data-mp="ddv6vbrs19gc">
          <div className="flex items-center gap-4 mb-6" data-id="element-1509" data-mp="112mv5s28x7nu">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1510" data-mp="w4la51l9ey9q">
              <CheckCircle size={24} data-id="element-1511" data-mp="32v2jk3z7ifps" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1512" data-mp="32vkpxtfl2abf">
              What Strong Self-Direction Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1513" data-mp="1ddf8vdbwauf6">
            People with strong self-direction and execution are more likely to
            set goals, create structure, follow through on commitments, manage
            time effectively, and keep moving even when motivation fluctuates.
            They do not depend entirely on pressure or supervision to act. They
            are better able to generate forward movement from within.
          </p>
        </motion.div>
      </section>

      {/* What Weak Self-Direction Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1514" data-mp="zntt0bte6vbd">
        <div className="max-w-4xl mx-auto" data-id="element-1515" data-mp="loc2ns0g79zw">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1516" data-mp="po2zaccnokcc">
            <div className="flex items-center gap-4 mb-6" data-id="element-1517" data-mp="3t03qkidtqtja">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1518" data-mp="3o0hcy7u33b9y">
                <XCircle size={24} data-id="element-1519" data-mp="3jxuhlwy7f880" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1520" data-mp="3k0qhvrxiy8zm">
                What Weak Self-Direction Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1521" data-mp="fj4hc5pu1usp">
              Weak self-direction often appears as inconsistency,
              procrastination, abandoned plans, chaotic routines, low
              follow-through, and difficulty converting ideas into sustained
              action. In many cases, the issue is not lack of ability but lack
              of structure, discipline, or reliable behavioral systems. Over
              time, this can create frustration, wasted talent, and
              underachievement.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1522" data-mp="11v60wauxkc2h">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1523" data-mp="16s6bmz2q62zx">
          <div className="flex items-center gap-4 mb-6" data-id="element-1524" data-mp="1kpl309cjgkuj">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1525" data-mp="1ppqplb3ybh5r">
              <Compass size={24} data-id="element-1526" data-mp="1uitidh0wmmkw" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1527" data-mp="1zglkf3v9jp2t">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1528" data-mp="23jhwebqasmxs">
            Self-direction and execution can be improved through habit systems,
            goal design, accountability structures, time management, coaching,
            milestone planning, and environments that reduce friction and
            support consistency. In the SyNeural framework, this capability is
            treated as crucial because judgment and talent only become
            meaningful when they can be translated into sustained action.
          </p>
        </motion.div>
      </section>
      <CapabilityNoetivraNote capability="Self-direction and execution" data-id="element-1529" data-mp="2b179ch84z9zq" />
    </div>;
}